import { createContext, useEffect, useState } from "react";
import useDatabase from "../hooks/useDatabase";
import argentineProvinces from "../utils/argentineProvinces";
import AddressTypeEnum from "../types/AddressTypeEnum";
import OptionProvince from "../types/OptionProvince";

interface AddressSendForm {
  address: string;
  addressDetails: string;
  location: string;
  province: OptionProvince | null;
  postalCode: string;
  additionalNotes: string;
}

interface AddressSendFormErrors {
  address?: string;
  addressDetails?: string;
  location?: string;
  province?: string;
  postalCode?: string;
  additionalNotes?: string;
}

interface AddressTypeProvider {
  selectedAddressType: AddressTypeEnum;
  setSelectedAddressType: React.Dispatch<React.SetStateAction<AddressTypeEnum>>;
}

interface AddressSendProvider {
  provinceOptions: OptionProvince[];
  addressSendForm: AddressSendForm;
  setAddressSendForm: React.Dispatch<React.SetStateAction<AddressSendForm>>;
  addressSendFormErrors: AddressSendFormErrors | null;
  validateAddressSendForm: () => boolean;
}

interface AddressPickupProvider {
  branches: any;
  selectedBranch: any;
  setSelectedBranch: React.Dispatch<React.SetStateAction<any>>;
}

export interface AddressContextProps {
  addressType: AddressTypeProvider;
  addressSend: AddressSendProvider;
  addressPickup: AddressPickupProvider;
  addressContextClear: any;
  getBranches: any
}

export const AddressContext = createContext(null);

export const AddressContextProvider = ({ children }: any) => {
  const { getCollection } = useDatabase();

  const [selectedAddressType, setSelectedAddressType] =
    useState<AddressTypeEnum>(AddressTypeEnum.SEND);

  const [addressSendFormErrors, setAddressSendFormErrors] =
    useState<AddressSendFormErrors | null>(null);

  const [branches, setBranches] = useState<any | null>([]);

  const [addressSendForm, setAddressSendForm] = useState<AddressSendForm>({
    address: "",
    addressDetails: "",
    location: "",
    province: null,
    postalCode: "",
    additionalNotes: "",
  });

  const [selectedBranch, setSelectedBranch] = useState<any>(null);

  const addressType = {
    selectedAddressType,
    setSelectedAddressType,
  };

  const validatePostalCodeNumber = (postalCode: string) => {
    const regexFourDigits = /^[a-zA-Z]\d{4}$/;
    return !regexFourDigits.test(postalCode) ? 'Requerido *' : null;
  };

  const validateAddressSendForm = () => {
    let hasErrors = false;
    const errors: AddressSendFormErrors = {};
    const requiredField = "Requerido *";

    if (!addressSendForm.address) {
      errors.address = requiredField;
      hasErrors = true;
    }

    if (!addressSendForm.location) {
      errors.location = requiredField;
      hasErrors = true;
    }

    if (!addressSendForm.province) {
      errors.province = requiredField;
      hasErrors = true;
    }

    if (!addressSendForm.postalCode) {
      errors.postalCode = requiredField;
      hasErrors = true;
    } else {
      const postalCodeError = validatePostalCodeNumber(addressSendForm.postalCode);
      if (postalCodeError) {
        errors.postalCode = postalCodeError;
        hasErrors = true;
      }
    }

    if (hasErrors) {
      setAddressSendFormErrors(errors);
      return false;
    }

    setAddressSendFormErrors(null);
    return true;
  };

  const addressSend = {
    provinceOptions: argentineProvinces,
    addressSendForm,
    setAddressSendForm,
    addressSendFormErrors,
    validateAddressSendForm,
  };

  const addressPickup = {
    branches,
    selectedBranch,
    setSelectedBranch,
  };

  const addressContextClear = () => {
    setSelectedAddressType(AddressTypeEnum.SEND);
    setAddressSendFormErrors(null);
    setBranches([]);
    setAddressSendForm({
      address: "",
      addressDetails: "",
      location: "",
      province: null,
      postalCode: "",
      additionalNotes: "",
    });
    setSelectedBranch(null);
  };

  const getBranches = async () => {
    const branches = (await getCollection("sucursal")).map((item: any) => ({
      ...item,
      schedule: JSON.parse(item.schedule),
    }));

    setBranches(branches);
  }

  // Global State like "Redux"
  const contextProps: AddressContextProps = {
    addressType,
    addressSend,
    addressPickup,
    addressContextClear,
    getBranches,
  };

  return (
    // @ts-ignore
    <AddressContext.Provider value={contextProps}>
      {children}
    </AddressContext.Provider>
  );
};
